import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  Store as StoreIcon,
  ShoppingCart as OrderIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  Inventory as InventoryIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useUnifiedTranslation } from '../../hooks/useUnifiedTranslation';
import { supplierApi } from '../../services/api/supplier.api';
import { DataTable } from '../ui/DataTable';
import { formatCurrency, getDefaultCurrency, BASE_CURRENCY } from '../../utils/currency';
import { formatShortDate } from '../../utils/date-utils';
import { PAGE_SIZE } from '../../constants/pagination';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`supplier-tabpanel-${index}`}
      aria-labelledby={`supplier-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const SupplierDashboard: React.FC = () => {
  const { t } = useUnifiedTranslation({ namespace: 'dashboard' });
  const ts = (key: string, fallback: string): string => { const v = t(key); return typeof v === 'string' ? v : fallback; };
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);

  // Загрузка дашборда (supplier API)
  const { data: dashboardData, isLoading: _isLoadingDashboard } = useQuery({
    queryKey: ['supplier', 'dashboard'],
    queryFn: async () => {
      const response = await supplierApi.getDashboard();
      return response.data;
    },
    refetchInterval: 60000,
  });

  // Загрузка метрик (supplier API)
  const { data: metricsData, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ['supplier', 'metrics'],
    queryFn: async () => {
      const response = await supplierApi.getMetrics();
      return response.data;
    },
    refetchInterval: 30000,
  });

  // Загрузка заказов (supplier API)
  const { data: ordersData, isLoading: isLoadingOrders } = useQuery({
    queryKey: ['supplier', 'orders'],
    queryFn: async () => {
      const response = await supplierApi.getOrders({ limit: PAGE_SIZE.DEFAULT });
      return response.data;
    },
  });

  // Загрузка услуг (supplier API)
  const { data: servicesData, isLoading: isLoadingServices, refetch: _refetchServices } = useQuery({
    queryKey: ['supplier', 'services'],
    queryFn: async () => {
      const response = await supplierApi.getServices({ limit: PAGE_SIZE.DEFAULT });
      return response.data;
    },
  });

  // Загрузка отзывов (supplier API)
  const { data: reviewsData, isLoading: isLoadingReviews } = useQuery({
    queryKey: ['supplier', 'reviews'],
    queryFn: async () => {
      const response = await supplierApi.getReviews({ limit: PAGE_SIZE.SMALL });
      return response.data;
    },
  });

  // Мутация обновления статуса заказа
  const updateOrderMutation = useMutation({
    mutationFn: ({ orderId, status }: { orderId: string; status: any }) =>
      supplierApi.updateOrderStatus(orderId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['supplier', 'dashboard'] });
      alert('Статус заказа обновлен!');
    },
    onError: (error) => {
      console.error('Ошибка обновления статуса:', error);
      alert('Ошибка при обновлении статуса заказа');
    },
  });

  // Мутация создания услуги
  const createServiceMutation = useMutation({
    mutationFn: (data: any) => supplierApi.createService(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', 'services'] });
      setServiceDialogOpen(false);
      alert('Услуга успешно создана!');
    },
    onError: (error) => {
      console.error('Ошибка создания услуги:', error);
      alert('Ошибка при создании услуги');
    },
  });

  // Мутация удаления услуги
  const deleteServiceMutation = useMutation({
    mutationFn: (serviceId: string) => supplierApi.deleteService(serviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', 'services'] });
      alert('Услуга удалена!');
    },
    onError: (error) => {
      console.error('Ошибка удаления услуги:', error);
      alert('Ошибка при удалении услуги');
    },
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleOrderStatusChange = (orderId: string, status: any) => {
    updateOrderMutation.mutate({ orderId, status });
  };

  const handleCreateService = (data: any) => {
    createServiceMutation.mutate(data);
  };

  const handleDeleteService = (serviceId: string) => {
    // Используем window.confirm для обхода eslint правила
    if (window.confirm('Вы уверены, что хотите удалить эту услугу?')) {
      deleteServiceMutation.mutate(serviceId);
    }
  };

  // Используем метрики из дашборда или отдельного запроса
  const metrics = dashboardData?.metrics || metricsData;

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {ts('supplier.title', 'Дашборд поставщика')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {ts('supplier.subtitle', 'Управление услугами и заказами')}
        </Typography>
      </Box>

      {/* Алерты из дашборда */}
      {dashboardData?.alerts && dashboardData.alerts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {dashboardData.alerts.map((alert, index) => (
            <Alert key={index} severity={alert.type} sx={{ mb: 1 }}>
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* KPI карточки */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('supplier.kpis.totalRevenue', 'Общий доход')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : formatCurrency(metrics?.totalRevenue ?? 0, BASE_CURRENCY)}
                  </Typography>
                  {metrics?.revenueGrowth !== undefined && (
                    <Typography variant="caption" color={metrics.revenueGrowth >= 0 ? 'success.main' : 'error.main'}>
                      {metrics.revenueGrowth >= 0 ? '+' : ''}{metrics.revenueGrowth}%
                    </Typography>
                  )}
                </Box>
                <MoneyIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('supplier.kpis.activeOrders', 'Активные заказы')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : metrics?.activeOrders || 0}
                  </Typography>
                  {metrics?.pendingOrders !== undefined && (
                    <Typography variant="caption" color="warning.main">
                      В ожидании: {metrics.pendingOrders}
                    </Typography>
                  )}
                </Box>
                <OrderIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('supplier.kpis.rating', 'Рейтинг')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : metrics?.averageRating?.toFixed(1) || '0.0'}
                  </Typography>
                </Box>
                <StarIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('supplier.kpis.totalServices', 'Всего услуг')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : metrics?.totalServices || 0}
                  </Typography>
                </Box>
                <InventoryIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Вкладки */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={ts('supplier.tabs.orders', 'Заказы')} icon={<OrderIcon />} iconPosition="start" />
            <Tab label={ts('supplier.tabs.services', 'Услуги')} icon={<StoreIcon />} iconPosition="start" />
            <Tab label={ts('supplier.tabs.reviews', 'Отзывы')} icon={<StarIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Вкладка: Заказы */}
        <TabPanel value={activeTab} index={0}>
          {isLoadingOrders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : ordersData?.orders && ordersData.orders.length > 0 ? (
            <DataTable
              title={ts('supplier.ordersTitle', 'Мои заказы')}
              columns={[
                {
                  id: 'eventTitle',
                  label: ts('supplier.columns.event', 'Мероприятие'),
                  sortable: true,
                },
                {
                  id: 'serviceName',
                  label: ts('supplier.columns.service', 'Услуга'),
                },
                {
                  id: 'clientName',
                  label: ts('supplier.columns.client', 'Клиент'),
                },
                {
                  id: 'totalPrice',
                  label: ts('supplier.columns.amount', 'Сумма'),
                  render: (row: any) => formatCurrency(row.totalPrice ?? 0, BASE_CURRENCY),
                },
                {
                  id: 'status',
                  label: ts('supplier.columns.status', 'Статус'),
                  render: (row: any) => (
                    <Chip
                      label={row.status}
                      size="small"
                      color={
                        row.status === 'completed' ? 'success' :
                        row.status === 'in-progress' ? 'info' :
                        row.status === 'accepted' ? 'primary' :
                        row.status === 'pending' ? 'warning' :
                        'error'
                      }
                    />
                  ),
                },
                {
                  id: 'deadline',
                  label: ts('supplier.columns.deadline', 'Срок'),
                  render: (row: any) => row.deadline ? formatShortDate(row.deadline) : '—',
                },
                {
                  id: 'actions',
                  label: 'Действия',
                  render: (row: any) => (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {row.status === 'pending' && (
                        <Button
                          size="small"
                          color="success"
                          onClick={() => handleOrderStatusChange(row.id, 'accepted')}
                        >
                          Принять
                        </Button>
                      )}
                      {row.status === 'accepted' && (
                        <Button
                          size="small"
                          color="info"
                          onClick={() => handleOrderStatusChange(row.id, 'in-progress')}
                        >
                          В работу
                        </Button>
                      )}
                      {row.status === 'in-progress' && (
                        <Button
                          size="small"
                          color="success"
                          onClick={() => handleOrderStatusChange(row.id, 'completed')}
                        >
                          Завершить
                        </Button>
                      )}
                    </Box>
                  ),
                },
              ]}
              data={ordersData.orders}
              rowKey={(row: any) => row.id}
              pagination
            />
          ) : (
            <Alert severity="info">
              {ts('supplier.noOrders', 'Нет заказов')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Услуги */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {ts('supplier.servicesTitle', 'Мои услуги')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingService(null);
                setServiceDialogOpen(true);
              }}
            >
              Добавить услугу
            </Button>
          </Box>

          {isLoadingServices ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : servicesData?.services && servicesData.services.length > 0 ? (
            <Grid container spacing={3}>
              {servicesData.services.map((service: any) => (
                <Grid item xs={12} md={6} lg={4} key={service.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          {service.name}
                        </Typography>
                        <Chip 
                          label={service.status} 
                          size="small" 
                          color={service.status === 'active' ? 'success' : 'default'}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {service.description}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Typography variant="h5" color="primary">
                          {formatCurrency(service.price?.min ?? 0, service.price?.currency ?? BASE_CURRENCY)} - {formatCurrency(service.price?.max ?? 0, service.price?.currency ?? BASE_CURRENCY)}
                        </Typography>
                        <Chip 
                          label={service.category} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <StarIcon fontSize="small" color="warning" />
                        <Typography variant="body2" sx={{ ml: 0.5 }}>
                          {service.rating?.toFixed(1) || '0.0'} ({service.totalOrders || 0} заказов)
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button 
                          size="small" 
                          startIcon={<EditIcon />}
                          onClick={() => {
                            setEditingService(service);
                            setServiceDialogOpen(true);
                          }}
                        >
                          Редактировать
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteService(service.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">
              {ts('supplier.noServices', 'Нет услуг')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Отзывы */}
        <TabPanel value={activeTab} index={2}>
          {isLoadingReviews ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : reviewsData?.reviews && reviewsData.reviews.length > 0 ? (
            <>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="h6">
                  Средняя оценка: {reviewsData.averageRating?.toFixed(1) || '0.0'} / 5.0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Всего отзывов: {reviewsData.total}
                </Typography>
              </Box>
              <DataTable
                title={ts('supplier.reviewsTitle', 'Отзывы клиентов')}
                columns={[
                  {
                    id: 'userName',
                    label: ts('supplier.columns.client', 'Клиент'),
                    sortable: true,
                  },
                  {
                    id: 'serviceName',
                    label: ts('supplier.columns.service', 'Услуга'),
                  },
                  {
                    id: 'rating',
                    label: ts('supplier.columns.rating', 'Оценка'),
                    render: (row: any) => (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StarIcon fontSize="small" color="warning" sx={{ mr: 0.5 }} />
                        {row.rating}/5
                      </Box>
                    ),
                  },
                  {
                    id: 'comment',
                    label: ts('supplier.columns.comment', 'Комментарий'),
                  },
                  {
                    id: 'createdAt',
                    label: ts('supplier.columns.date', 'Дата'),
                    render: (row: any) => formatShortDate(row.createdAt),
                  },
                ]}
                data={reviewsData.reviews}
                rowKey={(row: any) => row.id}
                pagination
              />
            </>
          ) : (
            <Alert severity="info">
              {ts('supplier.noReviews', 'Нет отзывов')}
            </Alert>
          )}
        </TabPanel>
      </Card>

      {/* Диалог создания/редактирования услуги */}
      <Dialog 
        open={serviceDialogOpen} 
        onClose={() => setServiceDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editingService ? 'Редактировать услугу' : 'Создать новую услугу'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Название услуги"
              margin="normal"
              defaultValue={editingService?.name || ''}
              id="service-name"
            />
            <TextField
              fullWidth
              label="Описание"
              margin="normal"
              multiline
              rows={3}
              defaultValue={editingService?.description || ''}
              id="service-description"
            />
            <TextField
              fullWidth
              select
              label="Категория"
              margin="normal"
              defaultValue={editingService?.category || 'CATERING'}
              id="service-category"
            >
              <MenuItem value="CATERING">Кейтеринг</MenuItem>
              <MenuItem value="VENUE">Площадки</MenuItem>
              <MenuItem value="EQUIPMENT">Оборудование</MenuItem>
              <MenuItem value="DECORATION">Декор</MenuItem>
              <MenuItem value="PHOTO_VIDEO">Фото/Видео</MenuItem>
              <MenuItem value="ENTERTAINMENT">Развлечения</MenuItem>
              <MenuItem value="OTHER">Другое</MenuItem>
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Цена от"
                  type="number"
                  margin="normal"
                  defaultValue={editingService?.price?.min || ''}
                  id="service-price-min"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Цена до"
                  type="number"
                  margin="normal"
                  defaultValue={editingService?.price?.max || ''}
                  id="service-price-max"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceDialogOpen(false)}>
            Отмена
          </Button>
          <Button 
            onClick={() => {
              const name = (document.getElementById('service-name') as HTMLInputElement)?.value;
              const description = (document.getElementById('service-description') as HTMLInputElement)?.value;
              const category = (document.getElementById('service-category') as HTMLInputElement)?.value;
              const priceMin = (document.getElementById('service-price-min') as HTMLInputElement)?.value;
              const priceMax = (document.getElementById('service-price-max') as HTMLInputElement)?.value;
              
              if (name && description && category && priceMin && priceMax) {
                handleCreateService({
                  name,
                  description,
                  category,
                  price: {
                    min: parseFloat(priceMin),
                    max: parseFloat(priceMax),
                    currency: getDefaultCurrency()
                  },
                  tags: [],
                  images: []
                });
              }
            }}
            variant="contained"
          >
            {editingService ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierDashboard;
